import React from "react";
import {
  NormalizedRow,
  RowStatus,
  SAFE_RAW_FIELD_LABELS,
  isTerminalRowStatus,
} from "../lib/rowContract";
import { AlertCircle, CheckCircle2, MoreHorizontal, Copy, HelpCircle } from "lucide-react";
import { Button } from "../../../components/ui/button";

/**
 * Status pill classes per real row status + a distinct fallback for unknown.
 */
const CLASSES: Record<RowStatus, string> = {
  VALID: "bg-emerald-50 text-emerald-800 border-emerald-200",
  INVALID: "bg-rose-50 text-rose-800 border-rose-200",
  DUPLICATE: "bg-amber-50 text-amber-800 border-amber-200",
  PENDING: "bg-slate-100 text-slate-700 border-slate-200",
  CREATED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  UPDATED: "bg-sky-50 text-sky-800 border-sky-200",
  SKIPPED: "bg-zinc-100 text-zinc-700 border-zinc-200",
  FAILED: "bg-rose-50 text-rose-800 border-rose-200",
};

const LABELS: Record<RowStatus, string> = {
  VALID: "Valid",
  INVALID: "Invalid",
  DUPLICATE: "Duplicate",
  PENDING: "Pending",
  CREATED: "Created",
  UPDATED: "Updated",
  SKIPPED: "Skipped",
  FAILED: "Failed",
};

export function RowStatusPill({ row }: { row: NormalizedRow }) {
  if (!row.status) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium bg-zinc-100 text-zinc-600 border-zinc-200"
        data-testid={`row-status-pill-${row.id}`}
        title={`Unknown status: ${row.rawStatus}`}
      >
        <HelpCircle className="w-3 h-3" />
        Unknown
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${CLASSES[row.status]}`}
      data-testid={`row-status-pill-${row.id}`}
      data-status={row.status}
    >
      {LABELS[row.status]}
    </span>
  );
}

function ResolutionBadge({ row }: { row: NormalizedRow }) {
  if (!row.resolution) return null;
  const labels: Record<string, string> = {
    skip: "Skip",
    create_anyway: "Create anyway",
    update_existing: "Update existing",
    manual_review: "Manual review",
  };
  const isBlocking =
    row.resolution === "manual_review" ||
    (row.status === "INVALID"); // invalid rows always block regardless
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
        isBlocking
          ? "bg-amber-50 text-amber-800 border-amber-200"
          : "bg-slate-100 text-slate-700 border-slate-200"
      }`}
      data-testid={`row-resolution-${row.id}`}
    >
      {labels[row.resolution] || row.resolution}
    </span>
  );
}

function ValuesPreview({ row }: { row: NormalizedRow }) {
  const entries = Object.entries(row.values);
  if (entries.length === 0)
    return <span className="text-slate-400 text-xs">— empty —</span>;
  const shown = entries.slice(0, 3);
  return (
    <div className="space-y-0.5 min-w-0" data-testid={`row-values-${row.id}`}>
      {shown.map(([k, v]) => (
        <div
          key={k}
          className="text-xs text-slate-700 truncate"
          title={`${SAFE_RAW_FIELD_LABELS[k as keyof typeof SAFE_RAW_FIELD_LABELS] ?? k}: ${v}`}
        >
          <span className="text-slate-500">
            {SAFE_RAW_FIELD_LABELS[k as keyof typeof SAFE_RAW_FIELD_LABELS] ?? k}:{" "}
          </span>
          <span className="font-mono">{v}</span>
        </div>
      ))}
      {entries.length > shown.length && (
        <div className="text-[10px] text-slate-400">
          +{entries.length - shown.length} more
        </div>
      )}
    </div>
  );
}

function ErrorsPreview({ row }: { row: NormalizedRow }) {
  if (row.errors.length === 0) return null;
  return (
    <ul
      className="space-y-0.5 min-w-0"
      aria-live="polite"
      data-testid={`row-errors-${row.id}`}
      aria-labelledby={`row-${row.id}-errors-label`}
    >
      <span id={`row-${row.id}-errors-label`} className="sr-only">
        Validation errors for row {row.rowIndex}
      </span>
      {row.errors.slice(0, 3).map((e, i) => (
        <li key={i} className="text-xs text-rose-700 flex items-start gap-1">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span className="truncate">
            {e.field ? <strong>{e.field}: </strong> : null}
            {e.code}
          </span>
        </li>
      ))}
      {row.errors.length > 3 && (
        <li className="text-[10px] text-slate-400">
          +{row.errors.length - 3} more
        </li>
      )}
    </ul>
  );
}

export type RowsTableProps = {
  rows: NormalizedRow[];
  loading: boolean;
  onOpenRow: (row: NormalizedRow) => void;
};

/**
 * Desktop = table, mobile = stacked cards. Kept in one file so the row
 * shape stays trivially consistent between layouts.
 */
export function RowsTable({ rows, loading, onOpenRow }: RowsTableProps) {
  if (loading) {
    return (
      <div className="text-sm text-slate-500" data-testid="rows-loading">
        Loading rows…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500"
        data-testid="rows-empty"
      >
        No rows match this filter.
      </div>
    );
  }

  return (
    <>
      {/* Desktop table (md+) */}
      <div
        className="hidden md:block border border-slate-200 rounded-lg overflow-hidden bg-white"
        data-testid="rows-table"
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2.5 font-medium w-16">Row</th>
              <th className="text-left px-3 py-2.5 font-medium w-28">Status</th>
              <th className="text-left px-3 py-2.5 font-medium">Values</th>
              <th className="text-left px-3 py-2.5 font-medium">Issues</th>
              <th className="text-right px-3 py-2.5 font-medium w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-t border-slate-200 align-top hover:bg-slate-50/50"
                data-testid={`row-${r.id}`}
              >
                <td className="px-3 py-2.5 text-slate-500 font-mono text-xs">
                  #{r.rowIndex}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-1">
                    <RowStatusPill row={r} />
                    <ResolutionBadge row={r} />
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <ValuesPreview row={r} />
                </td>
                <td className="px-3 py-2.5">
                  <ErrorsPreview row={r} />
                </td>
                <td className="px-3 py-2.5 text-right">
                  <RowOpenButton row={r} onOpen={onOpenRow} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked cards (< md) */}
      <div className="md:hidden space-y-2" data-testid="rows-cards">
        {rows.map((r) => (
          <div
            key={r.id}
            className="rounded-lg border border-slate-200 bg-white p-3"
            data-testid={`row-card-${r.id}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-slate-500 font-mono text-xs">#{r.rowIndex}</span>
              <RowStatusPill row={r} />
            </div>
            <div className="mt-2">
              <ValuesPreview row={r} />
            </div>
            <div className="mt-2">
              <ErrorsPreview row={r} />
            </div>
            <div className="mt-2 flex items-center justify-between">
              <ResolutionBadge row={r} />
              <RowOpenButton row={r} onOpen={onOpenRow} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function RowOpenButton({
  row,
  onOpen,
}: {
  row: NormalizedRow;
  onOpen: (row: NormalizedRow) => void;
}) {
  const disabled = !row.status || isTerminalRowStatus(row.status);
  const label =
    row.status === "DUPLICATE"
      ? "Resolve"
      : row.status === "INVALID"
      ? "Review"
      : row.status === "VALID"
      ? "Actions"
      : "View";
  return (
    <Button
      size="sm"
      variant="outline"
      className="rounded-full h-7 text-xs"
      disabled={disabled}
      onClick={() => onOpen(row)}
      data-testid={`row-open-${row.id}`}
      aria-label={`${label} row ${row.rowIndex}`}
    >
      <MoreHorizontal className="w-3 h-3 mr-1" /> {label}
    </Button>
  );
}
